/**
 * Per-customer approvers for ticket authorization, picked from the customer's connected
 * Microsoft 365 or Google Workspace directory. Names and emails come from that directory.
 */

export interface DirectoryUser {
  /** The directory's own id for the user (Entra object id or Google user id). */
  id: string;
  name: string;
  email: string;
}

export interface CustomerApprovers {
  admins: readonly DirectoryUser[];
  hrContacts: readonly DirectoryUser[];
}

/** Where approver candidates come from. Injected so the form can be rendered without a live tenant. */
export interface DirectorySource {
  hasConnectedDirectory: (customerId: string) => Promise<boolean>;
  searchUsers: (customerId: string, query: string) => Promise<DirectoryUser[]>;
}

export const EMPTY_CUSTOMER_APPROVERS: CustomerApprovers = { admins: [], hrContacts: [] };

export function isDirectoryUser(value: unknown): value is DirectoryUser {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.id === "string" && typeof candidate.name === "string" && typeof candidate.email === "string";
}

/** Keeps only well-formed users from an untrusted API response. */
export function toDirectoryUsers(response: unknown): DirectoryUser[] {
  const rows = Array.isArray(response)
    ? response
    : Array.isArray((response as { rows?: unknown })?.rows)
      ? (response as { rows: unknown[] }).rows
      : [];
  return rows.filter(isDirectoryUser);
}
